import _ from 'lodash';
import { useState } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';

import { type SalesPage as SalesPageType } from 'queries/graphql';

import { useQueryAdData } from 'utils/ad-data-context';
import { FunnelDataProvider, type SalesFunnelData } from 'utils/funnel-data-context';
import { PreviewDataProvider } from 'utils/preview-data-context';
import { PAGE_TYPES, useFunnelDetail, usePageView } from 'utils/gtm';
import * as rudder from 'utils/rudderstack';

import GlobalStyleV2 from 'generic/GlobalStyleV2';
import { FunnelFooter } from 'generic/Footer';

import DynamicHead from 'components/DynamicHead';
import DynamicBody from 'components/DynamicBody';
import DynamicBottom from 'components/DynamicBottom';
import PreviewModeBanner from 'components/PreviewModeBanner';

import bodySectionStyles from './sales-page.module.scss';

import { StickyAnchorProvider } from 'utils/sticky-anchor-context';
import { CartProvider } from 'utils/cart-context';
import { buildCssVar } from 'utils/style-override';
import { FontOverrideProvider } from 'utils/font-override-context';

import { HeaderComponents, Components } from './components';
import { useRemoteConfig } from 'utils/feature-flags';

export const buildPage = () => {
  function SalesPage({ preview, funnelData, pageData }: SalesPageParams) {
    useQueryAdData();
    // Get remote configuration context
    useRemoteConfig();
    const { merchantName, offersConfig, isAfterpaySupported } = funnelData || {};
    const { id, title, metaDescription, theme, favicon } = pageData || {};

    const { asPath } = useRouter();
    usePageView({ pageType: PAGE_TYPES.SALES_PAGE, path: asPath, merchantName });
    useFunnelDetail(offersConfig);

    rudder.usePageView({ pageType: PAGE_TYPES.SALES_PAGE, path: asPath, merchantName, pageId: id });

    const [anchorRef, setAnchorRef] = useState();
    const stickyAnchorValue = { setAnchorRef, anchorRef };

    if (_.isEmpty(pageData) || _.isEmpty(funnelData)) {
      return <span>404 - Page Content missing</span>;
    }

    return (
      <PreviewDataProvider preview={preview}>
        <FontOverrideProvider {...funnelData}>
          <FunnelDataProvider funnelData={funnelData}>
            <CartProvider>
              {preview && <PreviewModeBanner />}
              <DynamicBody />
              <div>
                <GlobalStyleV2 {...theme} />
                <DynamicHead metaDescription={metaDescription} />
                <Head>
                  <title>{title}</title>
                  <meta property="og:title" content={title}></meta>
                  <meta name="twitter:title" content={title}></meta>
                  <link rel="icon" href={favicon?.src} />
                </Head>
                <StickyAnchorProvider stickyAnchorElement={stickyAnchorValue}>
                  {isAfterpaySupported && (
                    <Script
                      src="https://js.afterpay.com/afterpay-1.x.js"
                      data-analytics-enabled
                      strategy="lazyOnload"
                    ></Script>
                  )}
                  <main data-cy="sales-page">
                    <HeaderComponents components={pageData.components} />
                    <div className={`root ${bodySectionStyles.body_section_container}`}>
                      <style jsx>
                        {`
                          .root {
                            ${buildCssVar(
                              '--background-color',
                              theme.backgroundColor,
                              'var(--default-background-color)'
                            )}
                          }
                        `}
                      </style>

                      <Components components={pageData.components} />
                    </div>
                  </main>
                </StickyAnchorProvider>

                {pageData.footer && <FunnelFooter {...pageData.footer} />}
              </div>
              <DynamicBottom />
            </CartProvider>
          </FunnelDataProvider>
        </FontOverrideProvider>
      </PreviewDataProvider>
    );
  }

  return SalesPage;
};

type SalesPageParams = {
  preview: boolean;
  funnelData: SalesFunnelData;
  pageData: SalesPageType;
};
